import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Loader2, Send, Users, CheckCircle2, XCircle, AlertCircle, Search, X, Bell, BellOff } from "lucide-react";
import { useSendNotificationMutation, useGetCustomersQuery } from "../../state/api";
import Header from "components/Header";
import {
  formatNotificationResult,
  isValidNotificationImageUrl,
} from "utils/notification";

const hasPushToken = (user) => Boolean(user?.fcmToken?.trim());

const Notifications = () => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [sendToAll, setSendToAll] = useState(true);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userPage, setUserPage] = useState(1);

  const [sendNotification, { isLoading }] = useSendNotificationMutation();
  const { data: customersResponse, isLoading: isLoadingUsers, isFetching } = useGetCustomersQuery({
    page: userPage,
    limit: 50,
    search: userSearchTerm.trim() || undefined,
  });

  const allUsers = customersResponse?.users || customersResponse?.data || [];
  const pagination = customersResponse?.pagination;
  const selectedUsers = allUsers.filter((user) => selectedUserIds.includes(user._id));

  useEffect(() => {
    setUserPage(1);
  }, [userSearchTerm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });

    if (!title.trim() || !body.trim()) {
      setMessage({ type: "error", text: "Title and body are required" });
      return;
    }

    if (!sendToAll && selectedUserIds.length === 0) {
      setMessage({ type: "error", text: "Please select at least one user or enable 'Send to all users'" });
      return;
    }

    if (!isValidNotificationImageUrl(imageUrl)) {
      setMessage({
        type: "error",
        text: "Image URL must be a valid http or https link. Leave it blank if you do not need an image.",
      });
      return;
    }

    try {
      const result = await sendNotification({
        title: title.trim(),
        body: body.trim(),
        ...(imageUrl.trim() && { imageUrl: imageUrl.trim() }),
        ...(sendToAll ? {} : { userIds: selectedUserIds }),
      }).unwrap();

      if (result.success && (result.sentCount || 0) > 0) {
        setMessage({
          type: "success",
          text: formatNotificationResult(result),
        });
        setTitle("");
        setBody("");
        setImageUrl("");
        setSelectedUserIds([]);
        setSendToAll(true);
      } else {
        setMessage({
          type: "error",
          text: formatNotificationResult(result),
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.data?.message || error?.message || "An error occurred while sending notification",
      });
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const selectAllOnPage = () => {
    const pageIds = allUsers.map((user) => user._id);
    setSelectedUserIds((prev) => Array.from(new Set([...prev, ...pageIds])));
  };

  const deselectAllUsers = () => setSelectedUserIds([]);

  const removeSelectedUser = (userId) => {
    setSelectedUserIds((prev) => prev.filter((id) => id !== userId));
  };

  return (
    <div className="p-6 space-y-6">
      <Header
        title="Send Push Notifications"
        subtitle="Send push notifications to users on the WishAur mobile app"
      />

      <Card>
        <CardHeader>
          <CardTitle>Notification Details</CardTitle>
          <CardDescription>
            Users must have opened the app at least once to receive push notifications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {message.text && (
              <div
                className={`p-4 rounded-lg flex items-start gap-3 ${
                  message.type === "success"
                    ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                    : "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
                }`}
              >
                {message.type === "success" ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                )}
                <p
                  className={`text-sm flex-1 ${
                    message.type === "success"
                      ? "text-green-800 dark:text-green-200"
                      : "text-red-800 dark:text-red-200"
                  }`}
                >
                  {message.text}
                </p>
                <button type="button" onClick={() => setMessage({ type: "", text: "" })}>
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="title" className="text-sm font-medium">
                Notification Title <span className="text-red-500">*</span>
              </label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter notification title"
                required
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="body" className="text-sm font-medium">
                Notification Message <span className="text-red-500">*</span>
              </label>
              <textarea
                id="body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Enter notification message"
                required
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="imageUrl" className="text-sm font-medium">
                Image URL (Optional)
              </label>
              <Input
                id="imageUrl"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Optional. Must be a full https:// or http:// link. Leave blank to send text only.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-gray-500" />
                  <div>
                    <label htmlFor="sendToAll" className="text-sm font-medium cursor-pointer">
                      Send to all users
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Broadcast to every user with a registered push token
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    id="sendToAll"
                    checked={sendToAll}
                    onChange={(e) => {
                      setSendToAll(e.target.checked);
                      if (e.target.checked) setSelectedUserIds([]);
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                </label>
              </div>

              {!sendToAll && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Selected Users: {selectedUserIds.length}</p>
                      <p className="text-xs text-gray-500">Choose specific users to notify</p>
                    </div>
                    <Button type="button" variant="outline" onClick={() => setIsUserDialogOpen(true)}>
                      <Users className="h-4 w-4 mr-2" />
                      Select Users
                    </Button>
                  </div>

                  {selectedUserIds.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border">
                      {selectedUsers.map((user) => (
                        <div
                          key={user._id}
                          className="flex items-center gap-2 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full text-sm"
                        >
                          <span>{user.name || user.email || user.phone || "Unknown"}</span>
                          <button type="button" onClick={() => removeSelectedUser(user._id)}>
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      {selectedUserIds.length > selectedUsers.length && (
                        <span className="text-xs text-gray-500 self-center">
                          +{selectedUserIds.length - selectedUsers.length} more from other pages
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end pt-4 border-t">
              <Button
                type="submit"
                disabled={
                  isLoading ||
                  !title.trim() ||
                  !body.trim() ||
                  (!sendToAll && selectedUserIds.length === 0)
                }
                className="min-w-[150px]"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Push Notification
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Select Users</DialogTitle>
            <DialogDescription>
              Search users and select recipients. Push status shows if the user can receive notifications.
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name, email, or phone..."
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={selectAllOnPage} disabled={allUsers.length === 0}>
                Select page
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={deselectAllUsers} disabled={selectedUserIds.length === 0}>
                Clear all
              </Button>
            </div>
            <p className="text-sm text-gray-500">{selectedUserIds.length} selected</p>
          </div>

          <div className="flex-1 overflow-auto border rounded-lg">
            {isLoadingUsers || isFetching ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : allUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-gray-500">
                <AlertCircle className="h-10 w-10 mb-2 opacity-50" />
                <p>No users found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12" />
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Push</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allUsers.map((user) => {
                    const isSelected = selectedUserIds.includes(user._id);
                    const pushEnabled = hasPushToken(user);
                    return (
                      <TableRow
                        key={user._id}
                        className={`cursor-pointer ${isSelected ? "bg-blue-50 dark:bg-blue-900/20" : ""}`}
                        onClick={() => toggleUserSelection(user._id)}
                      >
                        <TableCell>
                          <div
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              isSelected ? "bg-blue-600 border-blue-600" : "border-gray-300"
                            }`}
                          >
                            {isSelected && <CheckCircle2 className="h-4 w-4 text-white" />}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{user.name || "N/A"}</TableCell>
                        <TableCell>{user.email || "N/A"}</TableCell>
                        <TableCell>{user.phone || user.phoneNumber || "N/A"}</TableCell>
                        <TableCell>
                          {pushEnabled ? (
                            <span className="inline-flex items-center gap-1 text-xs text-green-700">
                              <Bell className="h-3.5 w-3.5" />
                              Enabled
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                              <BellOff className="h-3.5 w-3.5" />
                              No token
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>

          {pagination && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">
                Page {pagination.currentPage} of {pagination.totalPages || 1} · {pagination.totalUsers} users
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!pagination.hasPrevPage}
                  onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!pagination.hasNextPage}
                  onClick={() => setUserPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsUserDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                setIsUserDialogOpen(false);
                setUserSearchTerm("");
              }}
            >
              Done ({selectedUserIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Notifications;
